import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, RefreshCw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { SeverityBadge } from '@/components/domain/SeverityBadge';
import { BaseMap } from '@/components/map/BaseMap';
import { AlertDeliveryTable } from './AlertDeliveryTable';
import { useCrashDetail } from './useCrashDetail';
import { resendAlerts } from '@/api/endpoints/alerts';
import { queryKeys, POLL } from '@/api/queryKeys';
import { errorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';
import { absoluteTime, confidencePercent, coordinates, mapsLink } from '@/lib/format';

const SEVERITY_EXPLANATION = {
  minor: 'A low-force impact. The owner was notified.',
  major: 'A significant impact. The owner and fleet managers were notified.',
  critical: 'A severe impact. The owner and every emergency responder were notified.',
} as const;

export function CrashDetailPage() {
  const { crashId } = useParams<{ crashId: string }>();
  const numericId = Number(crashId);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [watching, setWatching] = useState(false);

  const { detail, isPending, isError, error, notFound, refetch } = useCrashDetail(numericId);

  const canResend = user?.role === 'emergency_responder' || user?.role === 'administrator';

  const resend = useMutation({
    mutationFn: () => resendAlerts(numericId),
    onSuccess: () => {
      // The endpoint returns 202: the work happens in a worker, so the outcome only
      // shows up by re-reading the alert rows.
      setWatching(true);
      toast.success('Re-dispatch queued. Delivery status will update here.');
    },
    onError: (caught) => toast.error(errorMessage(caught)),
  });

  // Poll the alert list while a re-dispatch is in flight, and stop once nothing is
  // pending — or after a minute, so a stuck worker does not poll forever.
  useEffect(() => {
    if (!watching) return;

    const interval = setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.alerts(500) });
    }, POLL.resendPending);

    const stop = setTimeout(() => setWatching(false), 60_000);

    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [watching, queryClient]);

  useEffect(() => {
    if (watching && detail && !detail.dispatchPending) setWatching(false);
  }, [watching, detail]);

  if (!Number.isInteger(numericId)) {
    return <ErrorState error={new Error('That crash address is not valid.')} title="Not found" />;
  }

  if (isPending) {
    return (
      <>
        <PageHeader title="Crash event" />
        <Card>
          <SkeletonRows rows={4} />
        </Card>
      </>
    );
  }

  if (isError) return <ErrorState error={error} onRetry={refetch} />;

  if (notFound || !detail) {
    return (
      <>
        <Link to="/crashes" className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-accent">
          <ArrowLeft className="size-4" aria-hidden />
          Back to crash events
        </Link>
        <Card>
          <EmptyState
            icon={ShieldAlert}
            title="This event is not in recent history"
            description="Crash records older than the current window cannot be opened directly. Ask an administrator if you need to retrieve an archived event."
          />
        </Card>
      </>
    );
  }

  const { crash, device, vehicle, alerts, failedCount } = detail;
  const hasPosition = crash.gps_lat !== null && crash.gps_lon !== null;

  return (
    <>
      <Link
        to="/crashes"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-accent hover:text-accent-hover"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Back to crash events
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <SeverityBadge severity={crash.severity} size="md" />
            <h1 className="text-xl font-semibold tracking-tight text-ink">
              {absoluteTime(crash.timestamp)}
            </h1>
          </div>
          <p className="mt-1.5 text-[13px] text-ink-secondary">
            {SEVERITY_EXPLANATION[crash.severity]} Scored at {confidencePercent(crash.confidence)}{' '}
            confidence by the impact rules.
          </p>
        </div>

        {canResend && (
          <Button
            variant={failedCount > 0 ? 'primary' : 'secondary'}
            loading={resend.isPending || watching}
            onClick={() => resend.mutate()}
          >
            <RefreshCw className="size-4" aria-hidden />
            Re-send alerts
          </Button>
        )}
      </div>

      {failedCount > 0 && (
        <Card className="mb-4 border-bad/30 bg-bad-soft">
          <CardBody>
            <p className="text-sm font-medium text-ink">
              {failedCount} of {alerts.length} alerts did not reach their destination
            </p>
            <p className="mt-0.5 text-[13px] text-ink-secondary">
              The reason for each failure is in the delivery table below.
            </p>
          </CardBody>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Where it happened" />
          {hasPosition ? (
            <div className="p-4">
              <BaseMap
                markers={[
                  {
                    id: crash.id,
                    position: [crash.gps_lat as number, crash.gps_lon as number],
                    title: `${crash.severity} impact`,
                    subtitle: absoluteTime(crash.timestamp),
                    tone: 'critical',
                  },
                ]}
              />
              <a
                href={mapsLink(crash.gps_lat as number, crash.gps_lon as number)}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-accent hover:text-accent-hover"
              >
                Open in Google Maps
                <ExternalLink className="size-3.5" aria-hidden />
              </a>
            </div>
          ) : (
            <EmptyState
              icon={ShieldAlert}
              title="No location recorded"
              description="The device did not have a GPS fix when the impact was detected."
            />
          )}
        </Card>

        <Card>
          <CardHeader title="Vehicle and device" />
          <CardBody className="space-y-3 text-[13px]">
            <div>
              <p className="text-ink-muted">Vehicle</p>
              <p className="mt-0.5 font-medium text-ink">
                {vehicle?.license_plate ?? 'Not available to your role'}
              </p>
              {vehicle && <p className="text-ink-secondary">{vehicle.vehicle_type}</p>}
            </div>
            <div>
              <p className="text-ink-muted">Device</p>
              {device ? (
                <Link
                  to={`/devices/${device.id}`}
                  className="mt-0.5 block font-medium text-accent hover:text-accent-hover"
                >
                  {device.serial_number}
                </Link>
              ) : (
                <p className="mt-0.5 text-ink-secondary">Not available to your role</p>
              )}
            </div>
            <div>
              <p className="text-ink-muted">Coordinates</p>
              <p className="mt-0.5 text-ink" data-numeric>
                {coordinates(crash.gps_lat, crash.gps_lon)}
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      <Card className="mt-4">
        <CardHeader
          title="Alert delivery"
          description="Every channel this event was dispatched through, and what happened to it."
        />
        <AlertDeliveryTable alerts={alerts} />
      </Card>
    </>
  );
}
